import React, { useState, useEffect } from 'react';
import { Play, Save, Download, Copy, Trash2, Plus, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { workflowBuilderService } from '../../services/workflowBuilderService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import TriggerLibrary from './components/TriggerLibrary';
import ActionNodePanel from './components/ActionNodePanel';
import WorkflowCanvas from './components/WorkflowCanvas';
import TestingSimulator from './components/TestingSimulator';

const AutomatedWorkflowBuilder = () => {
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowNodes, setWorkflowNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTestSimulator, setShowTestSimulator] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const [workflowForm, setWorkflowForm] = useState({
    workflowName: '',
    description: '',
    status: 'draft',
    triggerType: 'ai_compliance_prediction',
    triggerConditions: {}
  });

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [workflowsData, templatesData] = await Promise.all([
        workflowBuilderService?.getWorkflows(),
        workflowBuilderService?.getWorkflowTemplates()
      ]);
      setWorkflows(workflowsData);
      setTemplates(templatesData);
    } catch (err) {
      setError(err?.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedWorkflow(null);
    setWorkflowForm({
      workflowName: '',
      description: '',
      status: 'draft',
      triggerType: 'ai_compliance_prediction',
      triggerConditions: {}
    });
    setWorkflowNodes([]);
    setValidationErrors([]);
  };

  const handleLoadWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setWorkflowForm({
      workflowName: workflow?.workflowName,
      description: workflow?.description,
      status: workflow?.status,
      triggerType: workflow?.triggerType,
      triggerConditions: workflow?.triggerConditions || {}
    });
    setWorkflowNodes(workflow?.workflowNodes || []);
    setValidationErrors([]);
  };

  const handleLoadTemplate = (template) => {
    setSelectedWorkflow(null);
    setWorkflowForm({
      workflowName: template?.name,
      description: template?.description,
      status: 'draft',
      triggerType: template?.triggerType,
      triggerConditions: {}
    });
    setWorkflowNodes(template?.nodes || []);
    setValidationErrors([]);
  };

  const handleSaveWorkflow = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate workflow
      const validation = workflowBuilderService?.validateWorkflow({
        workflowNodes
      });

      if (!validation?.isValid) {
        setValidationErrors(validation?.errors);
        setError('Workflow validation failed. Please fix the errors.');
        return;
      }

      const workflowData = {
        ...workflowForm,
        workflowNodes
      };

      if (selectedWorkflow) {
        await workflowBuilderService?.updateWorkflow(selectedWorkflow?.id, workflowData);
        setSuccess('Workflow updated successfully');
      } else {
        const newWorkflow = await workflowBuilderService?.createWorkflow(workflowData);
        setSelectedWorkflow(newWorkflow);
        setSuccess('Workflow created successfully');
      }

      await loadData();
      setValidationErrors([]);
    } catch (err) {
      setError(err?.message || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;

    try {
      await workflowBuilderService?.deleteWorkflow(workflowId);
      setSuccess('Workflow deleted successfully');
      if (selectedWorkflow?.id === workflowId) {
        handleCreateNew();
      }
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to delete workflow');
    }
  };

  const handleToggleStatus = async (workflowId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await workflowBuilderService?.toggleWorkflowStatus(workflowId, newStatus);
      setSuccess(`Workflow ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to toggle workflow status');
    }
  };

  const handleExportWorkflow = async (workflowId) => {
    try {
      await workflowBuilderService?.exportWorkflow(workflowId);
      setSuccess('Workflow exported successfully');
    } catch (err) {
      setError(err?.message || 'Failed to export workflow');
    }
  };

  const handleAddNode = (node) => {
    setWorkflowNodes(prev => [...prev, { ...node, id: `node-${Date.now()}` }]);
  };

  const handleUpdateNode = (nodeId, updates) => {
    setWorkflowNodes(prev => prev?.map(node => 
      node?.id === nodeId ? { ...node, ...updates } : node
    ));
  };

  const handleDeleteNode = (nodeId) => {
    setWorkflowNodes(prev => prev?.filter(node => node?.id !== nodeId));
  };

  const handleTestWorkflow = () => {
    setShowTestSimulator(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'inactive': return 'text-gray-600 bg-gray-50';
      case 'draft': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Automated Workflow Builder</h1>
          <p className="text-gray-600 mt-1">
            Create intelligent response protocols for AI-predicted compliance violations and pest pressure spikes
          </p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
              {validationErrors?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {validationErrors?.map((err, idx) => (
                    <li key={idx} className="text-red-700 text-sm">• {err}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar - Workflow List & Templates */}
          <div className="col-span-12 lg:col-span-3 space-y-6">
            {/* Workflow Form */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Workflow Details</h2>
                <Button
                  onClick={handleCreateNew}
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  New
                </Button>
              </div>

              <div className="space-y-3">
                <Input
                  label="Workflow Name"
                  value={workflowForm?.workflowName}
                  onChange={(e) => setWorkflowForm(prev => ({ ...prev, workflowName: e?.target?.value }))}
                  placeholder="Enter workflow name"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={workflowForm?.description}
                    onChange={(e) => setWorkflowForm(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Describe this workflow"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <Select
                  label="Trigger Type"
                  value={workflowForm?.triggerType}
                  onChange={(e) => setWorkflowForm(prev => ({ ...prev, triggerType: e?.target?.value }))}
                  options={[
                    { value: 'ai_compliance_prediction', label: 'AI Compliance Prediction' },
                    { value: 'pest_pressure_spike', label: 'Pest Pressure Spike' },
                    { value: 'weather_alert', label: 'Weather Alert' },
                    { value: 'custom_condition', label: 'Custom Condition' }
                  ]}
                />

                <Select
                  label="Status"
                  value={workflowForm?.status}
                  onChange={(e) => setWorkflowForm(prev => ({ ...prev, status: e?.target?.value }))}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                />

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveWorkflow}
                    disabled={saving || !workflowForm?.workflowName}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  {selectedWorkflow && (
                    <Button
                      onClick={handleTestWorkflow}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      Test
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Saved Workflows */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Saved Workflows</h2>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {workflows?.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No workflows yet</p>
                ) : (
                  workflows?.map(workflow => (
                    <div
                      key={workflow?.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedWorkflow?.id === workflow?.id
                          ? 'border-green-500 bg-green-50' :'border-gray-200 hover:border-green-300'
                      }`}
                      onClick={() => handleLoadWorkflow(workflow)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{workflow?.workflowName}</h3>
                          <p className="text-xs text-gray-500 mt-1">
                            {workflow?.workflowNodes?.length || 0} nodes
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(workflow?.status)}`}>
                          {workflow?.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleToggleStatus(workflow?.id, workflow?.status);
                          }}
                          className="p-1 hover:bg-gray-100 rounded"
                          title={workflow?.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          <Zap className={`w-4 h-4 ${
                            workflow?.status === 'active' ? 'text-green-600' : 'text-gray-400'
                          }`} />
                        </button>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleExportWorkflow(workflow?.id);
                          }}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Export"
                        >
                          <Download className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleDeleteWorkflow(workflow?.id);
                          }}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Templates */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Templates</h2>
              <div className="space-y-2">
                {templates?.map(template => (
                  <div
                    key={template?.id}
                    className="p-3 rounded-lg border border-gray-200 hover:border-green-300 cursor-pointer transition-colors"
                    onClick={() => handleLoadTemplate(template)}
                  >
                    <div className="flex items-start gap-2">
                      <Copy className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-medium text-gray-900 text-sm">{template?.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">{template?.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center - Workflow Canvas */}
          <div className="col-span-12 lg:col-span-6">
            <WorkflowCanvas
              nodes={workflowNodes}
              onUpdateNode={handleUpdateNode}
              onDeleteNode={handleDeleteNode}
              triggerType={workflowForm?.triggerType}
            />
          </div>

          {/* Right Sidebar - Node Libraries */}
          <div className="col-span-12 lg:col-span-3 space-y-6">
            <TriggerLibrary
              triggerType={workflowForm?.triggerType}
              onAddNode={handleAddNode}
            />

            <ActionNodePanel
              onAddNode={handleAddNode}
            />
          </div>
        </div>
      </div>

      {/* Testing Simulator Modal */}
      {showTestSimulator && selectedWorkflow && (
        <TestingSimulator
          workflow={selectedWorkflow}
          nodes={workflowNodes}
          onClose={() => setShowTestSimulator(false)}
        />
      )}
    </div>
  );
};

export default AutomatedWorkflowBuilder;